import { useQuery } from "@tanstack/react-query"
import { Bot, Check, ChevronRight, Copy, Download } from "lucide-react"
import * as React from "react"
import { useSearchParams } from "react-router"

import { EmptyState, SkeletonText } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { IconButton } from "@/components/ui/icon-button"
import { SearchInput } from "@/components/ui/search-input"
import { useCurrentUser } from "@/features/auth"
import { canManageUsersAndKeys } from "@/lib/roles"
import { cn } from "@/lib/utils"

import { openApiQuery } from "./api"
import { agentInstructions } from "./agent-instructions"
import { IntegrationKeys } from "./integration-keys"
import { filterReference, toReference, type ApiReference, type Operation } from "./openapi-data"
import { highlightShell, TOKEN_CLASS } from "./shell-highlight"

type Tab = "keys" | "docs"

const TABS: { id: Tab; label: string }[] = [
  { id: "keys", label: "Key management" },
  { id: "docs", label: "API documentation" },
]

/*
   Two tabs: the integration keys (admin, as the backend's /api-keys is) and the documentation.
   The tab lives in the URL (`?tab=docs`), so a link can open the documentation directly.

   The API reference is rendered in the console from the schema itself, so it is always the
   deployed contract. Integrations call the same API with an integration key in `X-API-Key`; the
   guide above the reference is the one integration we document by hand — publishing evaluation
   runs — because it is a sequence of calls, which a list of endpoints does not show.
*/

export function IntegrationPage() {
  const schema = useQuery(openApiQuery())
  const canManage = canManageUsersAndKeys(useCurrentUser()?.role ?? null)
  const reference = React.useMemo(
    () => (schema.isSuccess ? toReference(schema.data) : null),
    [schema.isSuccess, schema.data],
  )

  const download = () => {
    if (!schema.isSuccess) return
    const blob = new Blob([JSON.stringify(schema.data, null, 2)], { type: "application/json" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = "vela-openapi.json"
    a.click()
    URL.revokeObjectURL(url)
  }

  const [params, setParams] = useSearchParams()
  const tab: Tab = canManage && params.get("tab") !== "docs" ? "keys" : "docs"
  const tabs = canManage ? TABS : TABS.filter((t) => t.id === "docs")

  return (
    <div className="flex h-full flex-col gap-5 overflow-auto px-6 py-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-subtitle text-text">Integrations</h1>
          <p className="mt-1 max-w-3xl text-body-sm text-text-2">
            Calling the API from another system.
            {reference && (
              <>
                {" "}
                {reference.title} {reference.version} · {reference.operationCount} operations.
              </>
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <CopyAgentInstructions reference={reference} />
          {tab === "docs" && (
            <Button
              variant="default"
              leadingIcon={<Download />}
              disabled={!schema.isSuccess}
              onClick={download}
            >
              Download openapi.json
            </Button>
          )}
        </div>
      </header>

      <div role="tablist" aria-label="Integrations" className="flex gap-1 border-b border-border">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={cn(
              "-mb-px border-b-2 px-3 py-2 text-body-sm",
              tab === t.id
                ? "border-brand font-semibold text-text"
                : "border-transparent text-text-3 hover:text-text-2",
            )}
            onClick={() => {
              setParams(t.id === "keys" ? {} : { tab: t.id }, { replace: true })
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "keys" && (
        <>
          <IntegrationKeys />
          <Authentication />
        </>
      )}
      {tab === "docs" && (
        <>
          <Authentication />
          <PublishingGuide />
          <Endpoints />
        </>
      )}
    </div>
  )
}

function Endpoints() {
  const schema = useQuery(openApiQuery())
  const [query, setQuery] = React.useState("")
  const reference = React.useMemo(
    () => (schema.isSuccess ? toReference(schema.data) : null),
    [schema.isSuccess, schema.data],
  )
  const tags = reference ? filterReference(reference, query) : []

  return (
    <section aria-label="Endpoints" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-heading text-text">Endpoints</h2>
        <SearchInput
          aria-label="Filter endpoints"
          placeholder="Filter by path, method or summary"
          className="w-80"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
          }}
          onClear={() => {
            setQuery("")
          }}
        />
      </div>
      {schema.isError && (
        <EmptyState
          reason="error"
          title="The schema could not be loaded"
          body={schema.error.message}
        />
      )}
      {schema.isPending && <SkeletonText lines={6} />}
      {reference && tags.length === 0 && <EmptyState title="No endpoint matches the filter" />}
      {tags.map((tag) => (
        <div key={tag.name} className="rounded-lg border border-border bg-surface">
          <div className="border-b border-border px-4 py-2.5">
            <h3 className="text-body font-semibold text-text">{tag.name}</h3>
            {tag.description && <p className="text-caption text-text-3">{tag.description}</p>}
          </div>
          <ul>
            {tag.operations.map((op) => (
              <OperationRow key={op.key} op={op} />
            ))}
          </ul>
        </div>
      ))}
    </section>
  )
}

const METHOD_TONE: Record<string, string> = {
  GET: "var(--info)",
  POST: "var(--master)",
  PATCH: "var(--print)",
  PUT: "var(--print)",
  DELETE: "var(--err)",
}

function MethodBadge({ method }: { method: string }) {
  const tone = METHOD_TONE[method] ?? "var(--text-3)"
  return (
    <span
      className="inline-flex w-16 shrink-0 justify-center rounded-sm border px-1.5 py-0.5 font-mono text-caption font-semibold"
      style={{ color: tone, borderColor: tone }}
    >
      {method}
    </span>
  )
}

function OperationRow({ op }: { op: Operation }) {
  return (
    <li className="border-b border-border last:border-0">
      <details className="group">
        <summary className="flex cursor-pointer list-none items-center gap-3 px-4 py-2 hover:bg-surface-sunken">
          <ChevronRight
            aria-hidden
            className="size-4 shrink-0 text-text-3 transition-transform group-open:rotate-90"
          />
          <MethodBadge method={op.method} />
          <code className="font-mono text-body-sm text-text">{op.path}</code>
          <span className="truncate text-body-sm text-text-3">{op.summary}</span>
        </summary>
        <div className="flex flex-col gap-3 px-11 pb-4 pt-1 text-body-sm">
          {op.description && <p className="whitespace-pre-line text-text-2">{op.description}</p>}
          {op.params.length > 0 && (
            <FieldTable
              title="Parameters"
              rows={op.params.map((p) => ({
                name: p.name,
                type: `${p.type} · ${p.where}`,
                required: p.required,
                description: p.description,
              }))}
            />
          )}
          {op.body && (
            <FieldTable
              title={`Body · ${op.body.mediaType}${op.body.schemaName ? ` · ${op.body.schemaName}` : ""}`}
              rows={op.body.fields}
            />
          )}
          <div>
            <h4 className="mb-1 text-caption font-semibold uppercase tracking-wide text-text-3">
              Responses
            </h4>
            <ul className="flex flex-col gap-0.5">
              {op.responses.map((r) => (
                <li key={r.status} className="flex gap-3">
                  <code className="w-10 font-mono text-text">{r.status}</code>
                  <span className="text-text-2">
                    {r.description}
                    {r.schemaName && (
                      <code className="ml-2 font-mono text-text-3">{r.schemaName}</code>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </details>
    </li>
  )
}

function FieldTable({
  title,
  rows,
}: {
  title: string
  rows: { name: string; type: string; required: boolean; description: string }[]
}) {
  return (
    <div>
      <h4 className="mb-1 text-caption font-semibold uppercase tracking-wide text-text-3">
        {title}
      </h4>
      {rows.length === 0 ? (
        <p className="text-text-3">No fields.</p>
      ) : (
        <table className="w-full text-left">
          <tbody>
            {rows.map((r) => (
              <tr key={r.name} className="border-t border-border align-top">
                <td className="w-56 py-1 pr-3">
                  <code className="font-mono text-text">{r.name}</code>
                  {r.required && <span className="ml-1 text-caption text-err">required</span>}
                </td>
                <td className="w-56 py-1 pr-3 font-mono text-caption text-text-2">{r.type}</td>
                <td className="py-1 text-text-2">{r.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

function Authentication() {
  return (
    <section className="rounded-lg border border-border bg-surface px-4 py-3">
      <h2 className="text-heading text-text">Calling the API</h2>
      <ul className="mt-2 flex list-disc flex-col gap-1 pl-5 text-body-sm text-text-2">
        <li>
          Base URL: <code className="font-mono text-text">{window.location.origin}</code>. Every
          path below is under it.
        </li>
        <li>
          Integrations authenticate with an <strong>integration key</strong> in the{" "}
          <code className="font-mono text-text">X-API-Key</code> header. An admin creates one under
          Key management; its value is shown once. Keep it in the environment, never on a command
          line or in a repository.
        </li>
        <li>
          Lists answer a bare array, with <code className="font-mono text-text">limit</code> /{" "}
          <code className="font-mono text-text">offset</code> and the total in{" "}
          <code className="font-mono text-text">X-Total-Count</code>, unless the endpoint returns a
          page object.
        </li>
      </ul>
    </section>
  )
}

const ENV_SNIPPET = `export PYXIS_API_URL=${typeof window === "undefined" ? "https://<host>" : window.location.origin}
export PYXIS_API_KEY=<integration key>`

const SCRIPT_SNIPPET = `# From a checkout of pyxis-rc-ai-service, after the run is scored
# (export → match → tiebreak; see docs/eval-runs.md).
scripts/eval-run.sh publish \\
  --workdir <metrics workdir> --dataset batch_0 --run <run> \\
  --data-dir <client batch directory with the label PDFs> \\
  --name "Batch 0, run <run>" --client-id <engagement id>     # add --dry to check first

scripts/eval-run.sh list                      # every run: id, state, labels, safety recall
scripts/eval-run.sh download <id> run.json    # one run: figures, marks, findings, remarks`

const CURL_SNIPPET = `# 1. Create the run (a draft; runs are admin-only)
curl -sS -X POST "$PYXIS_API_URL/api/v1/eval-runs" \\
  -H "X-API-Key: $PYXIS_API_KEY" -H "Content-Type: application/json" \\
  -d '{"name": "Batch 0, run l2v11", "dataset": "batch_0", "method": "Judged view: …",
       "summary": {"safety_recall": 0.69}, "client_id": 3}'

# 2. Upload each label: its marks and findings as JSON, and the PDF they were made on
curl -sS -X POST "$PYXIS_API_URL/api/v1/eval-runs/<id>/labels" \\
  -H "X-API-Key: $PYXIS_API_KEY" \\
  -F mid=054 -F product="ArmorTech PPZ 143 MC" -F kind=commented -F file_name=054.pdf \\
  -F payload=@054.json -F pdf=@054.pdf

# 3. Download the results, remarks included
curl -sS "$PYXIS_API_URL/api/v1/eval-runs/<id>/export" -H "X-API-Key: $PYXIS_API_KEY"`

function PublishingGuide() {
  return (
    <section className="rounded-lg border border-border bg-surface px-4 py-3">
      <h2 className="text-heading text-text">Publishing evaluation runs</h2>
      <p className="mt-1 max-w-3xl text-body-sm text-text-2">
        Results are published by an integration, never uploaded by hand: an integration key may
        create a run and upload its labels, under the{" "}
        <code className="font-mono text-text">eval-runs</code> endpoints below. The run starts as a
        draft; an admin then reads it in <strong>Evaluation runs</strong>, leaves remarks on any
        mark or finding, and moves it to Reviewed or Archived. Runs are admin-only and never shown
        to clients. A run that has not been through the tie-break is refused, so the figures are
        always the judged ones.
      </p>
      <ol className="mt-3 flex list-decimal flex-col gap-3 pl-5 text-body-sm text-text-2">
        <li>
          Set the API location and the integration key:
          <Snippet code={ENV_SNIPPET} />
        </li>
        <li>
          Publish with the script, which reads a scored metrics run and makes the calls below:
          <Snippet code={SCRIPT_SNIPPET} />
        </li>
        <li>
          Or call the API directly. The label payload is{" "}
          <code className="font-mono text-text">EvalLabelPayload</code> (marks with their judged
          outcome, findings with their boxes):
          <Snippet code={CURL_SNIPPET} />
        </li>
      </ol>
    </section>
  )
}

function Snippet({ code }: { code: string }) {
  const [copied, setCopied] = React.useState(false)
  return (
    <div className="relative mt-1.5">
      <pre
        className={cn(
          "overflow-x-auto rounded-md border border-border bg-surface-sunken px-3 py-2 pr-10",
          "font-mono text-caption leading-relaxed text-text",
        )}
      >
        <code>
          {highlightShell(code).map((line, i) => (
            <React.Fragment key={i}>
              {i > 0 && "\n"}
              {line.map((t, j) => (
                <span key={j} className={TOKEN_CLASS[t.kind]}>
                  {t.text}
                </span>
              ))}
            </React.Fragment>
          ))}
        </code>
      </pre>
      <IconButton
        label={copied ? "Copied" : "Copy"}
        icon={copied ? <Check /> : <Copy />}
        className="absolute right-1.5 top-1.5"
        onClick={() => {
          void navigator.clipboard.writeText(code).then(() => {
            setCopied(true)
            window.setTimeout(() => {
              setCopied(false)
            }, 1500)
          })
        }}
      />
    </div>
  )
}

function CopyAgentInstructions({ reference }: { reference: ApiReference | null }) {
  const [copied, setCopied] = React.useState(false)
  return (
    <Button
      variant="default"
      leadingIcon={copied ? <Check /> : <Bot />}
      title="A Markdown brief for an AI agent: base URL, how to authenticate, and how to publish"
      onClick={() => {
        void navigator.clipboard
          .writeText(agentInstructions(window.location.origin, reference))
          .then(() => {
            setCopied(true)
            window.setTimeout(() => {
              setCopied(false)
            }, 1500)
          })
      }}
    >
      {copied ? "Copied" : "Copy instructions for agents"}
    </Button>
  )
}
